"use client";

import { Button } from "@/components/ui/primitives/button";
import { CookiePreferencesModal } from "@/components/cookie-consent/cookie-preferences-modal";
import { COOKIE_POLICY_URL } from "@/lib/constants/legal";
import {
  clearPostHogCookies,
  clearRevokedStorage,
  hasValidConsent,
  readCookieConsent,
  writeCookieConsent,
  type CookieConsent,
} from "@/lib/utils/cookie-consent";
import { applyAnalyticsConsent } from "@/lib/utils/posthog-consent";
import { Cookie } from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [currentConsent, setCurrentConsent] = useState<CookieConsent | null>(
    null,
  );

  // Show the banner on mount if no valid consent exists.
  // Expire leftover ph_* cookies as soon as JS can run, including first
  // visits before the user answers the banner.
  useEffect(() => {
    const consent = readCookieConsent();
    setCurrentConsent(consent);
    clearPostHogCookies();
    if (!hasValidConsent()) {
      setVisible(true);
    } else if (consent) {
      // Consent already exists -- apply it silently
      applyAnalyticsConsent(consent.analytics);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  const handleSave = useCallback(
    (choices: Pick<CookieConsent, "functional" | "analytics">) => {
      const prev = readCookieConsent();
      const next = writeCookieConsent(choices);
      clearRevokedStorage(prev, next);
      applyAnalyticsConsent(next.analytics);
      setCurrentConsent(next);
      setVisible(false);
    },
    [],
  );

  const handleAcceptAll = useCallback(() => {
    handleSave({ functional: true, analytics: true });
  }, [handleSave]);

  const handleEssentialOnly = useCallback(() => {
    handleSave({ functional: false, analytics: false });
  }, [handleSave]);

  // Don't render anything while hidden (and modal closed)
  if (!visible && !prefsOpen) return null;

  return (
    <>
      {/* Banner */}
      {visible && (
        <section
          aria-label="Cookie consent"
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-5"
        >
          <div className="pointer-events-auto mx-auto max-w-5xl rounded-xl border border-separator-primary bg-white shadow-2xl">
            <div className="flex flex-col items-start gap-3 px-5 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-5 sm:py-3">
              <Cookie className="hidden h-4 w-4 shrink-0 text-text-secondary sm:block" />
              <p className="flex-1 text-sm text-text-secondary">
                We use cookies to keep the platform working and to understand
                how you use it.{" "}
                <Link
                  href={COOKIE_POLICY_URL}
                  className="underline underline-offset-2 hover:text-text-primary transition-colors"
                >
                  Learn more
                </Link>
              </p>
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleEssentialOnly}>
                  Essential Only
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPrefsOpen(true)}
                >
                  Customize
                </Button>
                <Button variant="default" size="sm" onClick={handleAcceptAll}>
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Preferences modal (can be opened from banner or externally) */}
      <CookiePreferencesModal
        open={prefsOpen}
        onOpenChange={setPrefsOpen}
        initialConsent={currentConsent}
        onSave={handleSave}
      />
    </>
  );
}
